(() => {
  const engineVersion = '2026-09-19-v7';
  if (window.ozanaScrollMotionVersion === engineVersion) return;
  window.ozanaScrollMotionVersion = engineVersion;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /*
   * One predictable motion engine for every OZANA page. IntersectionObserver is
   * intentionally used instead of a view timeline here: several visual sections
   * use overflow for their glass masks and horizontal rails, which can make the
   * nearest scroll timeline differ from the page viewport on mobile Safari.
   */
  const motionTargets = [
    {
      kind: 'intro',
      selectors: [
        '.ozana-shop-hero__glass',
        '.ozana-about__hero-copy',
        '.ozana-faq__hero-copy',
        '.ozana-contact__hero-inner',
        '.ozana-product-detail__media',
        '.ozana-product-detail__copy',
        '.ozana-legal-page__hero',
        '.cart__warnings',
        '.cart__contents'
      ]
    },
    {
      kind: 'rise',
      selectors: [
        '.ozana-products__heading',
        '.ozana-products__intro',
        '.ozana-products__note',
        '.ozana-filtration__steps',
        '.ozana-filtration__copy',
        '.ozana-filtration__cartridge',
        '.ozana-filtration__outro',
        '.ozana-filters__heading',
        '.ozana-filters__intro',
        '.ozana-filters__group-heading',
        '.ozana-filters__group-intro',
        '.ozana-filters__outro',
        '.ozana-spaces__header',
        '.ozana-spaces__statement',
        '.ozana-newsletter__mark',
        '.ozana-newsletter h2',
        '.ozana-newsletter > p',
        '.ozana-newsletter__form',
        '.ozana-offgrid__copy',
        '.ozana-offgrid__product',
        '.ozana-about__statement',
        '.ozana-about__section-heading',
        '.ozana-about__difference-heading',
        '.ozana-about__roadmap > .ozana-about__wrap > h2',
        '.ozana-about__roadmap-intro',
        '.ozana-about__roadmap-grid',
        '.ozana-about__cta .ozana-about__wrap',
        '.ozana-faq__certification',
        '.ozana-faq__reasons-head',
        '.ozana-faq__category-bar',
        '.ozana-faq__group-heading',
        '.ozana-faq__scene-shell > h2',
        '.ozana-faq__groups',
        '.ozana-faq__closing-inner',
        '.ozana-contact__standards',
        '.ozana-contact__support-heading',
        '.ozana-contact__form-intro',
        '.ozana-contact__contact-cards',
        '.ozana-contact__closing',
        '.ozana-product-detail__cartridge-copy',
        '.ozana-product-detail__cartridge-steps',
        '.ozana-product-detail__heading-row',
        '.ozana-product-detail__spec-cards',
        '.ozana-product-detail__maintenance .ozana-product-detail__wrap',
        '.ozana-product-detail__usage-layout',
        '.ozana-product-detail__reduction-list',
        '.ozana-product-detail__certified .ozana-product-detail__wrap',
        '.ozana-product-detail__bundle-offer',
        '.ozana-product-detail__benefits-heading',
        '.ozana-product-detail__cta',
        '.ozana-product-detail__info-grid',
        '.ozana-product-detail__notice',
        '.ozana-filtration-story__header',
        '.ozana-legal-page__toc',
        '.ozana-legal-page__content > h2',
        '.ozana-legal-page__product-grid',
        '.ozana-legal-page__steps',
        '.ozana-legal-page__definitions',
        '.ozana-legal-page__closing',
        '.cart__blocks',
        '.ozana-custom-footer__menu',
        '.ozana-custom-footer__brand',
        '.ozana-custom-footer__policies'
      ]
    },
    {
      kind: 'fade',
      selectors: [
        '.ozana-products__rail',
        '.ozana-filters__rail',
        '.ozana-spaces__grid',
        '.ozana-about__guide-track',
        '.ozana-faq__reason-track',
        '.ozana-about__difference-visual',
        '.ozana-contact__carousel',
        '.ozana-contact__form-panel',
        '.ozana-lifestyle-break__media',
        '.ozana-filtration-story__desktop-stage',
        '.ozana-filtration-story__mobile-visual',
        '.ozana-product-detail__filtration-image',
        '.ozana-product-detail__benefits-tabs',
        '.ozana-product-detail__benefits-panels',
        '.ozana-legal-page__content'
      ]
    },
    {
      kind: 'fade',
      selectors: [
        '.ozana-benefits__intro',
        '.ozana-benefits__grid',
        '.ozana-impact__card'
      ]
    },
    {
      kind: 'fade',
      media: '(min-width: 750px)',
      selectors: [
        '.ozana-about__metrics'
      ]
    }
  ];

  const cascadeGroups = [
    {
      root: '.ozana-benefits__intro',
      items: ':scope > .ozana-benefits__eyebrow, :scope > .ozana-benefits__title, :scope > .ozana-benefits__lead',
      step: 90,
      cap: 180
    },
    {
      root: '.ozana-benefits__grid',
      items: ':scope > .ozana-benefits__card',
      step: 85,
      cap: 255
    },
    {
      root: '.ozana-impact__card',
      items: ':scope > .ozana-impact__heading-group, :scope > .ozana-impact__content',
      step: 120,
      cap: 120
    },
    {
      root: '.ozana-product-detail__benefits-tabs',
      items: ':scope > button',
      step: 65,
      cap: 195
    },
    {
      root: '.ozana-shop-hero__glass',
      items: ':scope > h1, :scope > p, :scope > .ozana-shop-hero__actions',
      step: 90,
      cap: 180
    },
    {
      root: '.ozana-about__hero-copy',
      items: ':scope > .ozana-about__eyebrow, :scope > h1, :scope > .ozana-about__lead, :scope > .ozana-about__outline-button',
      step: 95,
      cap: 285
    },
    {
      root: '.ozana-faq__hero-copy',
      items: ':scope > h1, :scope > .ozana-faq__lead, :scope > .ozana-faq__outline-button',
      step: 100,
      cap: 200
    },
    {
      root: '.ozana-contact__hero-inner',
      items: ':scope > h1, :scope > .ozana-contact__lead, :scope > .ozana-contact__pill-link',
      step: 100,
      cap: 200
    },
    {
      root: '.ozana-products__rail',
      items: '.ozana-product-card',
      step: 70,
      cap: 210
    },
    {
      root: '.ozana-filters__rail',
      items: '.ozana-filter-card',
      step: 65,
      cap: 195
    },
    {
      root: '.ozana-about__metrics',
      items: ':scope > div',
      step: 90,
      cap: 180,
      media: '(min-width: 750px)'
    },
    {
      root: '.ozana-about__roadmap-grid',
      items: ':scope > article',
      step: 80,
      cap: 240,
      media: '(min-width: 750px)'
    },
    {
      root: '.ozana-faq__certification',
      items: ':scope > h2, :scope > p, .ozana-faq__certification-row',
      step: 70,
      cap: 140
    },
    {
      root: '.ozana-faq__reason-track',
      items: '.ozana-faq__reason-card',
      step: 90,
      cap: 180
    },
    {
      root: '.ozana-faq__groups',
      items: '.ozana-faq__group:not([hidden]) .ozana-faq__item',
      step: 55,
      cap: 220
    },
    {
      root: '.ozana-contact__standards',
      items: ':scope > h2, :scope > p, :scope > .ozana-contact__standards-badges',
      step: 70,
      cap: 140
    },
    {
      root: '.ozana-contact__carousel',
      items: '.ozana-contact__support-card',
      step: 75,
      cap: 150
    },
    {
      root: '.ozana-contact__contact-cards',
      items: ':scope > a, :scope > div',
      step: 65,
      cap: 130
    },
    {
      root: '.ozana-contact__form-panel',
      items: '.ozana-contact__field, .ozana-contact__form-row, button, .ozana-contact__privacy',
      step: 60,
      cap: 240
    },
    {
      root: '.cart__contents',
      items: '.cart-item',
      step: 70,
      cap: 210
    },
    {
      root: '.ozana-legal-page__hero',
      items: ':scope > .ozana-legal-page__eyebrow, :scope > h1, :scope > .ozana-legal-page__subtitle, :scope > .ozana-legal-page__meta',
      step: 85,
      cap: 255
    },
    {
      root: '.ozana-legal-page__content',
      items: ':scope > p, :scope > h2, :scope > h3, :scope > ul, :scope > ol, :scope > blockquote, :scope > div, :scope > dl',
      step: 38,
      cap: 190
    }
  ];

  const excludedSelector = [
    '[hidden]',
    '[aria-hidden="true"]',
    '.header-wrapper',
    '.menu-drawer',
    '.ozana-header-menu',
    '.ozana-hero__notifications',
    '.ozana-hero__notifications-track',
    '.cart-notification',
    '[aria-live]'
  ].join(',');

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && entry.boundingClientRect.top >= 0) return;
          reveal(entry.target);
        });
      }, { threshold: 0.06, rootMargin: '0px 0px -8% 0px' })
    : null;

  const ambientObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-ozana-ambient-active', entry.isIntersecting);
        });
      }, { threshold: 0, rootMargin: '12% 0px' })
    : null;

  function isExcluded(element) {
    return Boolean(element.matches(excludedSelector) || element.closest(excludedSelector));
  }

  function reveal(element) {
    if (!element || element.classList.contains('is-ozana-motion-visible')) return;
    element.classList.add('is-ozana-motion-visible');
    element.classList.remove('is-ozana-motion-pending');
    revealObserver?.unobserve(element);
  }

  function queueInitialReveal(element) {
    if (element.dataset.ozanaInitialRevealQueued === 'true') return;
    element.dataset.ozanaInitialRevealQueued = 'true';
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => reveal(element));
    });
  }

  function prepareMotionElement(element, kind) {
    if (element.dataset.ozanaScrollMotion === 'true' || isExcluded(element)) return;

    element.dataset.ozanaScrollMotion = 'true';
    element.dataset.ozanaMotionKind = kind;
    element.classList.add('ozana-scroll-motion');

    if (reducedMotion.matches || !revealObserver) {
      reveal(element);
      return;
    }

    element.classList.add('is-ozana-motion-pending');
    const bounds = element.getBoundingClientRect();
    const isInitiallyVisible = bounds.top < window.innerHeight && bounds.bottom > 0;

    if (isInitiallyVisible) {
      element.classList.add('is-ozana-motion-initial');
      queueInitialReveal(element);
    } else {
      revealObserver.observe(element);
    }
  }

  function prepareCascadeGroup(root, config) {
    if (config.media && !window.matchMedia(config.media).matches) return;

    root.classList.add('ozana-motion-cascade');
    const items = Array.from(root.querySelectorAll(config.items));
    items.forEach((item, index) => {
      if (item.dataset.ozanaCascadeItem === 'true') return;
      const entersExistingGroup = root.classList.contains('is-ozana-motion-visible');
      item.dataset.ozanaCascadeItem = 'true';
      item.classList.add('ozana-motion-cascade__item');
      item.style.setProperty('--ozana-cascade-delay', `${Math.min(index * config.step, config.cap)}ms`);

      if (entersExistingGroup && !reducedMotion.matches) {
        item.classList.add('is-ozana-cascade-pending');
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => item.classList.remove('is-ozana-cascade-pending'));
        });
      }
    });
  }

  function prepareAmbientCard(card) {
    if (card.dataset.ozanaAmbientMotion === 'true') return;
    card.dataset.ozanaAmbientMotion = 'true';
    card.classList.add('ozana-ambient-card');

    if (reducedMotion.matches) return;
    ambientObserver?.observe(card);

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    card.addEventListener('pointermove', (event) => {
      const bounds = card.getBoundingClientRect();
      card.style.setProperty('--ozana-glow-x', `${event.clientX - bounds.left}px`);
      card.style.setProperty('--ozana-glow-y', `${event.clientY - bounds.top}px`);
    }, { passive: true });
    card.addEventListener('pointerleave', () => {
      card.style.removeProperty('--ozana-glow-x');
      card.style.removeProperty('--ozana-glow-y');
    }, { passive: true });
  }

  function init(scope = document) {
    motionTargets.forEach(({ kind, selectors, media }) => {
      if (media && !window.matchMedia(media).matches) return;
      const query = selectors.join(',');
      const elements = scope.matches?.(query) ? [scope] : Array.from(scope.querySelectorAll(query));
      elements.forEach((element) => prepareMotionElement(element, kind));
    });

    cascadeGroups.forEach((config) => {
      const roots = scope.matches?.(config.root) ? [scope] : Array.from(scope.querySelectorAll(config.root));
      roots.forEach((root) => prepareCascadeGroup(root, config));
    });

    const ambientQuery = '.ozana-product-card, .ozana-filter-card';
    const cards = scope.matches?.(ambientQuery) ? [scope] : Array.from(scope.querySelectorAll(ambientQuery));
    cards.forEach(prepareAmbientCard);
  }

  function initAfterPageSetup(scope = document) {
    window.requestAnimationFrame(() => init(scope));
  }

  function refreshPendingObservers() {
    if (!revealObserver || reducedMotion.matches) return;
    document.querySelectorAll('.is-ozana-motion-pending').forEach((element) => {
      const bounds = element.getBoundingClientRect();
      if (bounds.top < window.innerHeight * 0.94) {
        reveal(element);
      } else {
        revealObserver.observe(element);
      }
    });
  }

  let pendingRefreshFrame = 0;
  function schedulePendingRefresh() {
    if (pendingRefreshFrame || reducedMotion.matches) return;
    pendingRefreshFrame = window.requestAnimationFrame(() => {
      pendingRefreshFrame = 0;
      refreshPendingObservers();
    });
  }

  function revealForFocus(event) {
    const pending = event.target.closest?.('.is-ozana-motion-pending');
    if (pending) reveal(pending);
  }

  let mutationFrame = 0;
  const mutationObserver = 'MutationObserver' in window
    ? new MutationObserver((mutations) => {
        const needsRefresh = mutations.some((mutation) =>
          mutation.addedNodes.length ||
          (mutation.type === 'attributes' && (mutation.attributeName === 'hidden' || mutation.attributeName === 'aria-hidden'))
        );
        if (!needsRefresh) return;
        window.cancelAnimationFrame(mutationFrame);
        mutationFrame = window.requestAnimationFrame(() => {
          init();
          refreshPendingObservers();
        });
      })
    : null;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAfterPageSetup();
      mutationObserver?.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'aria-hidden']
      });
    }, { once: true });
  } else {
    initAfterPageSetup();
    mutationObserver?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-hidden']
    });
  }

  reducedMotion.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    document.querySelectorAll('.is-ozana-motion-pending').forEach(reveal);
  });
  document.addEventListener('focusin', revealForFocus);
  document.addEventListener('shopify:section:load', (event) => initAfterPageSetup(event.target));
  document.addEventListener('shopify:section:select', (event) => initAfterPageSetup(event.target));
  window.addEventListener('pageshow', () => {
    initAfterPageSetup();
    window.requestAnimationFrame(refreshPendingObservers);
  }, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    initAfterPageSetup();
    window.requestAnimationFrame(refreshPendingObservers);
  });
  window.addEventListener('scroll', schedulePendingRefresh, { passive: true });
})();
